import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { useState, useEffect, useRef, useCallback } from "react";
import { apiService } from "../services/api.service";
import type { Student, Teacher } from "../services/api.service";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { UserList } from "./ui/user-list";
import { RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { DateRangePicker } from "./DateRangePicker";
import {
  SessionsGrid,
  RecordingTimeline,
  AudioPlayer,
  extractTimestampFromRecording,
  getRecentUtcRange,
  type AudioUser,
  type AudioSession,
  type AudioRecording,
} from "./audio-recordings";

const RECENT_WINDOW_DAYS = 30;
// Each extra month is another full month-prefix sweep on every later request.
// The enforcing cap is MAX_RANGE_DAYS in src/api/audio-recordings.ts; this
// stops short of it so widening never lands on a 400.
const MAX_WINDOW_DAYS = 365;
const SESSIONS_PAGE_SIZE = 30;

export const AudioRecordingsDashboard = () => {
  // State
  const [directoryFilters, setDirectoryFilters] = useState<FilterValues>({});
  const [userScopes, setUserScopes] = useState<Map<string, { organizationId?: string | null; role: string }>>(new Map());
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { apiService.getOrganizations().then(setOrganizations).catch(() => {}); }, []);
  const [users, setUsers] = useState<AudioUser[]>([]);
  const [sessions, setSessions] = useState<AudioSession[]>([]);
  const [recordings, setRecordings] = useState<AudioRecording[]>([]);
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(
    new Map(),
  );

  const [selectedUser, setSelectedUser] = useState<AudioUser | null>(null);
  const [selectedSession, setSelectedSession] = useState<AudioSession | null>(
    null,
  );
  const [currentPlayingUrl, setCurrentPlayingUrl] = useState<string | null>(
    null,
  );
  const [currentPlayingRecording, setCurrentPlayingRecording] =
    useState<AudioRecording | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isTimelineOpen, setIsTimelineOpen] = useState(true);
  const [timelineHeight, setTimelineHeight] = useState(220);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // startDate/endDate are the picker's draft; only Apply promotes them to
  // appliedRange, which is what both panels load from.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedRange, setAppliedRange] = useState<{
    start_date: string;
    end_date: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [windowDays, setWindowDays] = useState(RECENT_WINDOW_DAYS);

  const [sessionsCursor, setSessionsCursor] = useState<string | null>(null);
  // Identifies which (user, window) the on-screen session list belongs to, so
  // an in-flight page can tell whether it is still wanted when it lands.
  const sessionsRequestRef = useRef("");

  // Loading states
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
  const [loadingRecordings, setLoadingRecordings] = useState(false);

  const { setActions } = useHeaderActions();
  const nameGeneration = useRef(0);

  // Load user names from students and teachers
  const loadUserNames = useCallback(async () => {
    const generation = ++nameGeneration.current;
    setUserNameMap(new Map());
    setUserScopes(new Map());
    try {
      const [students, teachers] = await Promise.all([
        apiService.getStudents(directoryFilters.organization_id || undefined),
        apiService.getTeachers(directoryFilters.organization_id || undefined),
      ]);

      const nameMap = new Map<string, string>();
      const scopes = new Map<string, { organizationId?: string | null; role: string }>();
      students.forEach((student: Student) => {
        nameMap.set(student.user_id.toString(), student.name);
        scopes.set(String(student.user_id), { organizationId: student.organization_id, role: student.account_role || "STUDENT" });
      });
      teachers.forEach((teacher: Teacher) => {
        nameMap.set(teacher.user_id.toString(), teacher.name);
        scopes.set(String(teacher.user_id), { organizationId: teacher.organization_id, role: teacher.account_role || "TEACHER" });
      });

      if (generation === nameGeneration.current) {
        setUserNameMap(nameMap);
        setUserScopes(scopes);
      }
    } catch (error) {
      console.error("Failed to fetch user names", error);
    }
  }, [directoryFilters.organization_id]);

  // One window drives both panels.
  const activeRange = appliedRange ?? getRecentUtcRange(windowDays);
  const rangeStart = activeRange.start_date;
  const rangeEnd = activeRange.end_date;
  const sessionsRequest = `${selectedUser?.user_id ?? ""}|${rangeStart}|${rangeEnd}`;

  // The server omits users with no recordings in range, so the sidebar needs
  // no per-user follow-up.
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.getAudioUsers({
        start_date: rangeStart,
        end_date: rangeEnd,
      });
      setUsers(response.users);
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [rangeStart, rangeEnd]);

  // Get display name for a user
  const getUserDisplayName = (userId: string): string => {
    const name = userNameMap.get(userId);
    return name || `User ${userId}`;
  };

  // Refresh names and scope metadata when the organization changes.
  useEffect(() => {
    loadUserNames();
    return () => { nameGeneration.current += 1; };
  }, [loadUserNames]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!selectedUser) {
      setSessions([]);
      setSessionsCursor(null);
      return;
    }

    sessionsRequestRef.current = sessionsRequest;

    let cancelled = false;
    const fetchSessions = async () => {
      setLoadingSessions(true);
      try {
        const response = await apiService.getUserSessions(
          selectedUser.user_id,
          {
            start_date: rangeStart,
            end_date: rangeEnd,
            limit: SESSIONS_PAGE_SIZE,
          },
        );
        if (cancelled) return;
        setSessions(response.sessions);
        setSessionsCursor(response.next_cursor ?? null);
      } catch (error) {
        console.error("Failed to fetch sessions", error);
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    };

    fetchSessions();
    return () => {
      cancelled = true;
    };
  }, [selectedUser, rangeStart, rangeEnd, sessionsRequest]);

  const loadMoreSessions = async () => {
    if (!selectedUser || !sessionsCursor || loadingMoreSessions) return;
    const request = sessionsRequest;
    setLoadingMoreSessions(true);
    try {
      const response = await apiService.getUserSessions(selectedUser.user_id, {
        start_date: rangeStart,
        end_date: rangeEnd,
        limit: SESSIONS_PAGE_SIZE,
        before: sessionsCursor,
      });
      // Nothing disables the sidebar while this is in flight. If the selection
      // or window moved on, these rows belong to a list that is no longer on
      // screen -- appending them would mix two users together.
      if (sessionsRequestRef.current !== request) return;
      setSessions((previous) => {
        const seen = new Set(previous.map((s) => s.session_id));
        return [
          ...previous,
          ...response.sessions.filter((s) => !seen.has(s.session_id)),
        ];
      });
      setSessionsCursor(response.next_cursor ?? null);
    } catch (error) {
      console.error("Failed to load more sessions", error);
      toast.error("Failed to load more sessions");
    } finally {
      setLoadingMoreSessions(false);
    }
  };

  // Load Recordings when Session Selected
  useEffect(() => {
    if (!selectedSession || !selectedUser) {
      setRecordings([]);
      return;
    }

    const fetchRecordings = async () => {
      setLoadingRecordings(true);
      try {
        const response = await apiService.getSessionRecordings(
          selectedSession.session_id,
          { user_id: selectedUser.user_id, dates: selectedSession.dates },
        );
        // Sort recordings by extracted timestamp from turn_id
        const sortedRecordings = response.recordings.sort((a, b) => {
          const dateA = extractTimestampFromRecording(a);
          const dateB = extractTimestampFromRecording(b);
          if (!dateA || !dateB) return 0;
          return dateA.getTime() - dateB.getTime();
        });
        setRecordings(sortedRecordings);
      } catch (error) {
        console.error("Failed to fetch recordings", error);
      } finally {
        setLoadingRecordings(false);
      }
    };

    fetchRecordings();
  }, [selectedSession, selectedUser]);

  // Handlers
  const handleUserClick = (user: AudioUser) => {
    setSelectedUser(user);
    setSelectedSession(null);
    setRecordings([]);
    setCurrentPlayingUrl(null);
    setCurrentPlayingRecording(null);
  };

  const handleSessionClick = (session: AudioSession) => {
    setSelectedSession(session);
    setCurrentPlayingUrl(null);
    setCurrentPlayingRecording(null);
  };

  const handlePlayRecording = async (recording: AudioRecording) => {
    try {
      // If clicking on the same recording, toggle play/pause
      if (currentPlayingRecording?.key === recording.key) {
        if (isPlaying) {
          audioRef.current?.pause();
        } else {
          audioRef.current?.play();
        }
        return;
      }

      // Check if we already have a presigned url in the object, otherwise fetch it
      let url = recording.presigned_url;
      if (!url) {
        const response = await apiService.getPlaybackUrl(recording.key);
        url = response.url;
      }
      setCurrentPlayingUrl(url);
      setCurrentPlayingRecording(recording);
      setIsPlaying(true);
    } catch (error) {
      console.error("Failed to get playback URL", error);
      toast.error("Failed to play recording");
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setCurrentPlayingUrl(null);
    setCurrentPlayingRecording(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    // Auto-play next recording if available
    if (currentPlayingRecording) {
      const currentIndex = recordings.findIndex(
        (r) => r.key === currentPlayingRecording.key,
      );
      const nextRecording = recordings[currentIndex + 1];
      if (
        currentIndex >= 0 &&
        currentIndex < recordings.length - 1 &&
        nextRecording
      ) {
        handlePlayRecording(nextRecording);
      }
    }
  };

  const handleNextRecording = () => {
    if (currentPlayingRecording) {
      const currentIndex = recordings.findIndex(
        (r) => r.key === currentPlayingRecording.key,
      );
      const nextRecording = recordings[currentIndex + 1];
      if (
        currentIndex >= 0 &&
        currentIndex < recordings.length - 1 &&
        nextRecording
      ) {
        handlePlayRecording(nextRecording);
      }
    }
  };

  const handlePreviousRecording = () => {
    if (currentPlayingRecording) {
      const currentIndex = recordings.findIndex(
        (r) => r.key === currentPlayingRecording.key,
      );
      const prevRecording = recordings[currentIndex - 1];
      if (currentIndex > 0 && prevRecording) {
        handlePlayRecording(prevRecording);
      }
    }
  };

  // The effects above reload on window change; these only set it and clear
  // what the old window had selected.
  const handleApplyFilters = () => {
    // A half-filled range means that single day.
    setAppliedRange({
      start_date: startDate || endDate,
      end_date: endDate || startDate,
    });
    setSelectedUser(null);
    setSelectedSession(null);
    setRecordings([]);
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setAppliedRange(null);
    setSearchTerm("");
    setWindowDays(RECENT_WINDOW_DAYS);
    setSelectedUser(null);
    setSelectedSession(null);
    setRecordings([]);
  };

  const loadEarlierUsers = () => {
    setWindowDays((days) =>
      Math.min(days + RECENT_WINDOW_DAYS, MAX_WINDOW_DAYS),
    );
  };

  const handleCloseTimeline = () => {
    setIsTimelineOpen(false);
  };

  // Reopen timeline when a new session is selected
  useEffect(() => {
    if (selectedSession) {
      setIsTimelineOpen(true);
    }
  }, [selectedSession]);

  const hasDraftFilters = !!(startDate || endDate);
  const hasActiveFilters = appliedRange !== null;
  const canLoadEarlierUsers = !hasActiveFilters && windowDays < MAX_WINDOW_DAYS;

  // Set header actions
  useEffect(() => {
    setActions(
      <div className="flex items-center gap-3">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-8"
            onClick={handleApplyFilters}
            disabled={!hasDraftFilters}
          >
            Apply
          </Button>
          {(hasDraftFilters || hasActiveFilters) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearFilters}
              title="Clear filters"
              className="h-8 w-8"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
        <Separator orientation="vertical" className="h-5" />
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="rounded-full px-2.5 py-0.5 text-xs"
            title={`${rangeStart} to ${rangeEnd}`}
          >
            {hasActiveFilters ? "Custom range" : `Last ${windowDays} days`}
          </Badge>
          <Badge className="rounded-full border border-lime-600/20 bg-lime-600/10 px-2.5 py-0.5 text-xs text-lime-900">
            {users.length} users
          </Badge>
          {selectedUser && (
            <Badge
              variant="outline"
              className="rounded-full px-2.5 py-0.5 text-xs"
            >
              {getUserDisplayName(selectedUser.user_id)}
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={loadUsers}
          disabled={loadingUsers}
          className="h-8 w-8"
        >
          <RefreshCw
            className={`h-4 w-4 ${loadingUsers ? "animate-spin" : ""}`}
          />
        </Button>
      </div>,
    );
    return () => setActions(null);
  }, [
    setActions,
    loadingUsers,
    loadUsers,
    users.length,
    selectedUser,
    startDate,
    endDate,
    hasDraftFilters,
    hasActiveFilters,
    rangeStart,
    rangeEnd,
    windowDays,
  ]);

  return (
    // pb-40 accounts for audio player (~80px) + some extra when timeline is open
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      <div className="mx-auto flex h-full w-full flex-col gap-4 overflow-hidden">
        {/* Main Content */}
        <div className="grid flex-1 min-h-0 grid-cols-[288px_1fr] gap-4 overflow-hidden max-lg:grid-cols-1">
          {/* Left Sidebar - Users */}
          <UserList
            users={users.filter((user) => {
              const scope = userScopes.get(String(user.user_id));
              return (!directoryFilters.organization_id || scope?.organizationId === directoryFilters.organization_id) && (!directoryFilters.role || scope?.role === directoryFilters.role);
            })}
            searchControls={<AdvancedOptions values={directoryFilters} onChange={setDirectoryFilters} fields={[
              { key: "organization_id", label: "Organization", options: organizations.map((org) => ({ value: org.id, label: org.name })) },
              { key: "role", label: "Account role", options: [{ value: "STUDENT", label: "Student" }, { value: "TEACHER", label: "Teacher" }, { value: "ORG_ADMIN", label: "Org Admin" }] },
            ]} />}
            isLoading={loadingUsers}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onUserSelect={handleUserClick}
            getUserId={(user) => user.user_id}
            getDisplayName={(user) => getUserDisplayName(user.user_id)}
            isSelected={(user) => selectedUser?.user_id === user.user_id}
            className="flex min-h-0 flex-col overflow-hidden border border-border/60 bg-white/80 shadow-sm"
            footer={
              !canLoadEarlierUsers ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={loadEarlierUsers}
                  disabled={loadingUsers}
                >
                  Load earlier recordings
                </Button>
              )
            }
          />

          {/* Right Panel - Sessions */}
          <SessionsGrid
            sessions={sessions}
            selectedUser={selectedUser}
            selectedSession={selectedSession}
            loading={loadingSessions}
            loadingMore={loadingMoreSessions}
            hasMore={sessionsCursor !== null}
            onSessionClick={handleSessionClick}
            onLoadMore={loadMoreSessions}
          />
        </div>

        {/* Timeline Section */}
        <RecordingTimeline
          recordings={recordings}
          selectedSession={selectedSession}
          currentPlayingRecording={currentPlayingRecording}
          loading={loadingRecordings}
          isOpen={isTimelineOpen}
          height={timelineHeight}
          onClose={handleCloseTimeline}
          onHeightChange={setTimelineHeight}
          onPlayRecording={handlePlayRecording}
        />

        {/* Audio Player */}
        <AudioPlayer
          currentPlayingUrl={currentPlayingUrl}
          currentPlayingRecording={currentPlayingRecording}
          recordings={recordings}
          selectedUser={selectedUser}
          selectedSession={selectedSession}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          audioRef={audioRef}
          onPlayPause={handlePlayPause}
          onClose={handleClosePlayer}
          onSeek={handleSeek}
          onPrevious={handlePreviousRecording}
          onNext={handleNextRecording}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleAudioEnded}
          onPlay={() => setIsPlaying(true)}
          onPauseEvent={() => setIsPlaying(false)}
          getUserDisplayName={getUserDisplayName}
        />
      </div>
    </div>
  );
};
